/**
 * Douglas–Peucker simplification, run again on every zoom change.
 *
 * `PLAN.md` §6 Phase 2 sets the target: a 20k-point track pans at 60fps. Drawing
 * every fix is not that — most of them are a straight line's-width apart at any
 * zoom that shows the whole flight — so the map keeps only the points whose
 * deviation from the simplified line would be visible at the current zoom, and
 * redoes the reduction each time the zoom changes enough to matter.
 *
 * Distance is measured in a locally flat approximation: longitude scaled by
 * `cos(meanLatitude)` before a plain Euclidean distance, which is accurate
 * enough to decide "does this bend show at this zoom" without a full
 * geodesic — the thing golden-file tests and duplicate detection need,
 * simplification does not.
 */

/** First and last index are always kept; nothing here drops the endpoints. */
export function douglasPeucker(
  lat: Float64Array,
  lon: Float64Array,
  epsilonDeg: number,
): Uint32Array {
  const count = lat.length;
  if (count <= 2) {
    return Uint32Array.from({ length: count }, (_, i) => i);
  }

  const cosLat = Math.cos((meanLatitude(lat) * Math.PI) / 180);
  const keep = new Uint8Array(count);
  keep[0] = 1;
  keep[count - 1] = 1;

  // Explicit stack rather than recursion: a near-straight 20k-point track is
  // the worst case for call-stack depth, and it is also the common case.
  const stack: [number, number][] = [[0, count - 1]];
  while (stack.length > 0) {
    const frame = stack.pop();
    if (frame === undefined) {
      break;
    }
    const [start, end] = frame;
    if (end - start < 2) {
      continue;
    }

    let farthestIndex = -1;
    let farthestDist = 0;
    for (let i = start + 1; i < end; i += 1) {
      const dist = perpendicularDistance(lat, lon, cosLat, start, end, i);
      if (dist > farthestDist) {
        farthestDist = dist;
        farthestIndex = i;
      }
    }

    if (farthestIndex !== -1 && farthestDist > epsilonDeg) {
      keep[farthestIndex] = 1;
      stack.push([start, farthestIndex]);
      stack.push([farthestIndex, end]);
    }
  }

  const kept: number[] = [];
  for (let i = 0; i < count; i += 1) {
    if (keep[i] === 1) {
      kept.push(i);
    }
  }
  return Uint32Array.from(kept);
}

function meanLatitude(lat: Float64Array): number {
  let sum = 0;
  for (const value of lat) {
    sum += value;
  }
  return lat.length === 0 ? 0 : sum / lat.length;
}

function perpendicularDistance(
  lat: Float64Array,
  lon: Float64Array,
  cosLat: number,
  start: number,
  end: number,
  i: number,
): number {
  const ay = lat[start] ?? 0;
  const ax = (lon[start] ?? 0) * cosLat;
  const by = lat[end] ?? 0;
  const bx = (lon[end] ?? 0) * cosLat;
  const py = lat[i] ?? 0;
  const px = (lon[i] ?? 0) * cosLat;

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return Math.hypot(px - ax, py - ay);
  }

  /*
   * Distance to the *segment* a–b, not to the infinite line through it. This
   * used to measure against the infinite line, on the reasoning that clamping
   * would under-simplify a track that loops back on itself. That is exactly
   * backwards, and it is the bug that made parts of a track vanish when zoomed
   * out and reappear on the way in.
   *
   * Fly 20nm up a valley and back down it and the first and last fix sit a few
   * hundred metres apart, with every fix in between lying almost *on* the line
   * through them — including the one at the far end of the leg. Measured
   * against the infinite line its deviation is near zero, so it fell under
   * epsilon, so the recursion stopped there and the whole out-and-back
   * collapsed to the short line joining its two ends. Zooming in shrank epsilon
   * until the leg's small lateral wander cleared it, which is why the missing
   * half of the flight "came back".
   *
   * Clamping to the segment makes that far point what it plainly is: 20nm from
   * the line being tested, so it is kept, so the recursion carries on into both
   * legs. A closed circuit, where a and b coincide, falls to the zero-length
   * branch above and is measured from the shared endpoint.
   */
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSquared));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** MapLibre defines its zoom against 512px tiles, not the 256px web-mercator convention. */
const WORLD_PIXELS_AT_ZOOM_0 = 512;

/** Deviation smaller than this many screen pixels is not worth drawing. */
const TOLERANCE_PX = 2;

/**
 * Degrees-of-latitude epsilon for a MapLibre zoom level.
 *
 * At zoom `z` the whole world is `512 * 2^z` pixels wide — MapLibre's zoom is
 * defined against 512px tiles, not the 256px convention Google and the older
 * Mapbox docs use, and taking the 256 figure meant tolerating twice the
 * deviation intended at every zoom.
 *
 * A deviation under ~2 px is invisible, so that is the target: generous enough
 * that panning stays smooth, tight enough that the track never visibly cuts a
 * corner.
 */
export function epsilonForZoom(zoom: number): number {
  const degreesPerPixel = 360 / (WORLD_PIXELS_AT_ZOOM_0 * Math.pow(2, zoom));
  return degreesPerPixel * TOLERANCE_PX;
}
